import React from "react";
import axios from "axios";
import { useTable } from "react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

export default function FTicketTable() {
  const [pendingRows, setPendingRows] = React.useState([]);
  const [completedRows, setCompletedRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [tabIndex, setTabIndex] = React.useState(0);
  const [selectedTicket, setSelectedTicket] = React.useState(null);
  const [openDetailDialog, setOpenDetailDialog] = React.useState(false);

  // Function to parse details from serial number
  const parseSerialNumber = (serial) => {
    try {
      const [ids, timestamp] = serial.split('-');
      const [fromID, toID] = ids.split('_');
      return { fromID, toID, timestamp };
    } catch (error) {
      console.error("Error parsing serial number:", error);
      return { fromID: 'N/A', toID: 'N/A', timestamp: serial };
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Serial Number",
        accessor: "serial",
        Cell: ({ value }) => value || "N/A"
      },
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ value }) => value || "N/A"
      },
      {
        Header: "From",
        accessor: "fromID",
        Cell: ({ value }) => value || "N/A"
      },
      {
        Header: "To",
        accessor: "toID",
        Cell: ({ value }) => value || "N/A"
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value || "N/A"
      },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleViewDetails(row.original)}
            style={{ backgroundColor: "#904dd3", color: "#fff" }}
          >
            View Details
          </Button>
        )
      }
    ],
    []
  );

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      axios.defaults.withCredentials = true;
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/ticket/get-tickets`);

      // Process active and resolved tickets with details
      const processTickets = async (tickets, status) => {
        return Promise.all(tickets.map(async (serial) => {
          try {
            // Fetch ticket details using POST method
            const detailResponse = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/ticket/get-ticket-details`,
              {
                ticketID: serial
              }
            );

            const { fromID, toID, timestamp } = parseSerialNumber(serial);

            return {
              serial,
              fromID,
              toID,
              title: detailResponse.data.ticket.title || 'N/A',
              body: detailResponse.data.ticket.body || 'N/A',
              status,
              createdAt: timestamp
            };
          } catch (error) {
            console.error(`Error fetching details for ticket ${serial}:`, error);
            const { fromID, toID, timestamp } = parseSerialNumber(serial);
            return {
              serial,
              fromID,
              toID,
              title: 'N/A',
              body: 'N/A',
              status,
              createdAt: timestamp
            };
          }
        }));
      };

      const formattedActiveTickets = await processTickets(response.data.active_tickets, "Active");
      const formattedResolvedTickets = await processTickets(response.data.resolved_tickets, "Resolved");

      setPendingRows(formattedActiveTickets);
      setCompletedRows(formattedResolvedTickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTickets();
  }, []);

  const handleTabChange = (index) => {
    setTabIndex(index);
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedTicket(null);
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;

    try {
      axios.defaults.withCredentials = true;

      await axios.post(`${process.env.REACT_APP_BASE_URL}/ticket/update-ticket-status`, {
        ticketID: selectedTicket.serial,
        status: "Closed"
      });

      // Remove from active tickets and add to completed
      const updatedPendingRows = pendingRows.filter(row => row.serial !== selectedTicket.serial);
      const updatedTicket = { ...selectedTicket, status: "Closed" };

      setPendingRows(updatedPendingRows);
      setCompletedRows([...completedRows, updatedTicket]);

      // Close dialog
      handleCloseDetailDialog();
    } catch (error) {
      console.error("Error closing ticket:", error);
      // Show error message to the user
      alert(error.response?.data?.message || "Failed to close ticket");
    }
  };

  const TableComponent = ({ data }) => {
    const tableInstance = useTable({
      columns,
      data: data || []
    });

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow
    } = tableInstance;

    if (isLoading) {
      return <div>Loading tickets...</div>;
    }

    if (!data || !data.length) {
      return <div>No tickets available</div>;
    }

    return (
      <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: columnKey, ...columnProps } = column.getHeaderProps();
                  return (
                    <th
                      key={columnKey}
                      {...columnProps}
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#f5f5f5",
                        textAlign: "left",
                        color: "black",
                      }}
                    >
                      {column.render("Header")}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { key: rowKey, ...rowProps } = row.getRowProps();
            return (
              <tr key={rowKey} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps();
                  return (
                    <td
                      key={cellKey}
                      {...cellProps}
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #ddd",
                        color: "black",
                        maxWidth: cell.column.Header === "Title" ? "200px" : "auto",
                        overflow: cell.column.Header === "Title" ? "hidden" : "visible",
                        textOverflow: cell.column.Header === "Title" ? "ellipsis" : "clip",
                        whiteSpace: cell.column.Header === "Title" ? "nowrap" : "normal"
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div style={styles.card}>
      {/* Ticket Details Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ticket Details</DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <>
              <Typography variant="h6">Serial Number: {selectedTicket.serial}</Typography>
              <Typography variant="body1"><strong>Title:</strong> {selectedTicket.title}</Typography>
              <Typography variant="body1"><strong>Description:</strong> {selectedTicket.body}</Typography>
              <Typography variant="body1"><strong>From:</strong> {selectedTicket.fromID}</Typography>
              <Typography variant="body1"><strong>To:</strong> {selectedTicket.toID}</Typography>
              <Typography variant="body1"><strong>Status:</strong> {selectedTicket.status}</Typography>
              <Typography variant="body1"><strong>Created At:</strong> {selectedTicket.createdAt}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTicket && selectedTicket.status === "Active" && (
            <Button
              onClick={handleCloseTicket}
              color="secondary"
              variant="contained"
              style={{ backgroundColor: "#904dd3", color: "#fff" }}
            >
              Close Ticket
            </Button>
          )}
          <Button onClick={handleCloseDetailDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <div>
        <Button
          onClick={() => handleTabChange(0)}
          style={{
            backgroundColor: tabIndex === 0 ? "#904dd3" : "#f5f5f5",
            color: tabIndex === 0 ? "#fff" : "#000",
            marginRight: "10px",
          }}
        >
          Active Tickets
        </Button>
        <Button
          onClick={() => handleTabChange(1)}
          style={{
            backgroundColor: tabIndex === 1 ? "#904dd3" : "#f5f5f5",
            color: tabIndex === 1 ? "#fff" : "#000",
          }}
        >
          Resolved Tickets
        </Button>
      </div>

      {tabIndex === 0 && <TableComponent data={pendingRows} />}
      {tabIndex === 1 && <TableComponent data={completedRows} />}
    </div>
  );
}

const styles = {
  card: {
    width: "100%",
    height: "100%",
    position: "relative",
    padding: "20px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    borderRadius: "10px",
  },
};